import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

/**
 * The one place the app is allowed to write to the console.
 *
 * There were 300+ bare `console.log` calls across the app, many of them printing
 * whole API payloads — plan contents, channel lists, and in the chat service the
 * user's own campaign brief. In production that is both noise and a disclosure:
 * anything logged is readable by anyone who opens devtools on a shared machine,
 * and it is retained by any error-reporting tool that scrapes the console.
 *
 * `debug` and `info` compile to no-ops in production. `warn` and `error` always
 * run, because a silenced error is worse than a noisy one — those are the two
 * levels a support engineer actually needs when a user reports a problem.
 *
 * This is deliberately NOT a wrapper that also ships logs somewhere. Adding a
 * remote sink is a separate decision with its own privacy question, and building
 * the seam first is what makes that decision possible later without touching 300
 * call sites again.
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly enabled = !environment.production;

  debug(...args: unknown[]): void {
    if (this.enabled) console.debug(...args);
  }

  info(...args: unknown[]): void {
    if (this.enabled) console.info(...args);
  }

  /** Always emitted — a warning suppressed in production is a warning nobody acts on. */
  warn(...args: unknown[]): void {
    console.warn(...args);
  }

  /** Always emitted. */
  error(...args: unknown[]): void {
    console.error(...args);
  }
}
